import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {
    EditRounded,
    KeyRounded,
    PersonAddAltRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button, IconButton } from "../components/Buttons";
import {
    getAssignableUsers,
    inviteOrgUser,
    listOrgRoles,
    listUsers,
    resetUserPassword,
    setUserStatus,
    updateUser,
} from "../api";
import { formatDateTime } from "../utils/format";
import { apiError } from "../utils/meta";

const fullName = (row) =>
    `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || row.email;

/* ------------------------------------------------------------------ */
/* Invite dialog — the only way new users enter the org                 */
/* ------------------------------------------------------------------ */

function InviteDialog({ open, roles, assignable, onClose, onInvited }) {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        orgRoleId: "",
        managerId: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                firstName: "",
                lastName: "",
                email: "",
                orgRoleId: "",
                managerId: "",
            });
        }
    }, [open]);

    const set = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    async function handleInvite() {
        if (!form.firstName.trim() || !form.email.trim() || !form.orgRoleId) {
            toast.error("First name, email and role are required");
            return;
        }
        setSending(true);
        try {
            const body = {
                firstName: form.firstName.trim(),
                email: form.email.trim(),
                orgRoleId: form.orgRoleId,
            };
            if (form.lastName.trim()) body.lastName = form.lastName.trim();
            if (form.managerId) body.managerId = form.managerId;
            const { data } = await inviteOrgUser(body);
            toast.success(data?.message || "Invitation sent");
            onInvited();
        } catch (err) {
            toast.error(apiError(err, "Could not send the invite"));
        } finally {
            setSending(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                Invite a teammate
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2, pt: 1 }}>
                    <Stack sx={{ flexDirection: "row", gap: 2 }}>
                        <TextField
                            label="First name"
                            value={form.firstName}
                            onChange={set("firstName")}
                            required
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Last name"
                            value={form.lastName}
                            onChange={set("lastName")}
                            fullWidth
                        />
                    </Stack>
                    <TextField
                        label="Work email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Role"
                        select
                        value={form.orgRoleId}
                        onChange={set("orgRoleId")}
                        required
                        fullWidth
                    >
                        {roles.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Manager (optional)"
                        select
                        value={form.managerId}
                        onChange={set("managerId")}
                        fullWidth
                    >
                        <MenuItem value="">No manager</MenuItem>
                        {assignable.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                                {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
                                    u.email}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={sending}
                    onClick={handleInvite}
                >
                    Send invite
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Edit drawer                                                          */
/* ------------------------------------------------------------------ */

function EditDrawer({ user, roles, assignable, onClose, onSaved }) {
    const open = !!user;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!user) return;
        setForm({
            firstName: user.first_name ?? "",
            lastName: user.last_name ?? "",
            mobileNumber: user.mobile_number ?? "",
            designation: user.designation ?? "",
            orgRoleId: user.org_role_id ?? "",
            managerId: user.manager_id ?? "",
            isActive: user.is_active !== false,
        });
    }, [user]);

    const set = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    async function handleSave() {
        if (!form.firstName?.trim()) {
            toast.error("First name is required");
            return;
        }
        setSaving(true);
        try {
            await updateUser(user.id, {
                firstName: form.firstName.trim(),
                lastName: form.lastName?.trim() || null,
                mobileNumber: form.mobileNumber?.trim() || null,
                designation: form.designation?.trim() || null,
                orgRoleId: form.orgRoleId || undefined,
                managerId: form.managerId || null,
                isActive: form.isActive,
            });
            toast.success("User updated");
            onSaved();
        } catch (err) {
            toast.error(apiError(err, "Could not update the user"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: "100%", md: 460 }, background: "var(--bg)" },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <Stack
                    sx={{
                        p: "24px 28px 20px",
                        borderBottom: "1px solid var(--border)",
                        gap: 0.5,
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        EDIT USER
                    </Typography>
                    <Typography variant="h5">
                        {user ? fullName(user) : ""}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 13, color: "var(--text-secondary)" }}
                    >
                        {user?.email}
                    </Typography>
                </Stack>

                <Stack sx={{ flex: 1, overflowY: "auto", p: "24px 28px", gap: 2 }}>
                    <Stack sx={{ flexDirection: "row", gap: 2 }}>
                        <TextField
                            label="First name"
                            value={form.firstName ?? ""}
                            onChange={set("firstName")}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Last name"
                            value={form.lastName ?? ""}
                            onChange={set("lastName")}
                            fullWidth
                        />
                    </Stack>
                    <TextField
                        label="Mobile number"
                        value={form.mobileNumber ?? ""}
                        onChange={set("mobileNumber")}
                        fullWidth
                    />
                    <TextField
                        label="Designation"
                        value={form.designation ?? ""}
                        onChange={set("designation")}
                        fullWidth
                    />
                    <TextField
                        label="Role"
                        select
                        value={form.orgRoleId ?? ""}
                        onChange={set("orgRoleId")}
                        fullWidth
                    >
                        {roles.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Manager"
                        select
                        value={form.managerId ?? ""}
                        onChange={set("managerId")}
                        fullWidth
                    >
                        <MenuItem value="">No manager</MenuItem>
                        {assignable
                            .filter((u) => u.id !== user?.id)
                            .map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
                                        u.email}
                                </MenuItem>
                            ))}
                    </TextField>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            border: "1px solid var(--border-mid)",
                            borderRadius: "4px",
                            p: "10px 14px",
                        }}
                    >
                        <Stack sx={{ gap: 0.25 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                Active
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                Inactive users can no longer sign in.
                            </Typography>
                        </Stack>
                        <Switch
                            checked={form.isActive !== false}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    isActive: e.target.checked,
                                }))
                            }
                        />
                    </Stack>
                </Stack>

                <Stack
                    sx={{
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        p: "16px 28px",
                        borderTop: "1px solid var(--border)",
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Button variant="soft-outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        loading={saving}
                        onClick={handleSave}
                    >
                        Save changes
                    </Button>
                </Stack>
            </Stack>
        </Drawer>
    );
}

/* ------------------------------------------------------------------ */
/* Reset password dialog                                                */
/* ------------------------------------------------------------------ */

function ResetPasswordDialog({ user, onClose }) {
    const open = !!user;
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setPassword("");
    }, [open]);

    async function handleReset() {
        if (password.length < 8) {
            toast.error("Use at least 8 characters");
            return;
        }
        setBusy(true);
        try {
            await resetUserPassword(user.id, password);
            toast.success(`Password reset for ${fullName(user)}`);
            onClose();
        } catch (err) {
            toast.error(apiError(err, "Could not reset the password"));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                Reset password
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2, pt: 1 }}>
                    <Typography
                        sx={{ fontSize: 14, color: "var(--text-secondary)" }}
                    >
                        Set a new password for{" "}
                        <b>{user ? fullName(user) : ""}</b>. Share it with them
                        over a secure channel.
                    </Typography>
                    <TextField
                        label="New password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={busy}
                    onClick={handleReset}
                >
                    Reset password
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminUsers() {
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
    });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [assignable, setAssignable] = useState([]);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [resetting, setResetting] = useState(null);

    const load = useCallback(async (page = 1, limit = 25, searchTerm = "") => {
        setLoading(true);
        try {
            const { data } = await listUsers({
                page,
                limit,
                search: searchTerm,
            });
            setRows(data.data ?? []);
            setPagination(data.pagination ?? { page, limit, total: 0 });
        } catch (err) {
            toast.error(apiError(err, "Failed to load users"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(
            () => load(1, pagination.limit, search),
            search ? 350 : 0,
        );
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, load]);

    const loadPickers = useCallback(async () => {
        try {
            const [rolesRes, assignableRes] = await Promise.all([
                listOrgRoles(),
                getAssignableUsers(),
            ]);
            const roleList = Array.isArray(rolesRes.data)
                ? rolesRes.data
                : (rolesRes.data?.data ?? []);
            const userList = Array.isArray(assignableRes.data)
                ? assignableRes.data
                : (assignableRes.data?.data ?? []);
            setRoles(roleList);
            setAssignable(userList);
        } catch (error) {
            console.error("Failed to load role/manager pickers", { error });
        }
    }, []);

    useEffect(() => {
        loadPickers();
    }, [loadPickers]);

    const reload = () => load(pagination.page, pagination.limit, search);

    async function handleStatusToggle(row) {
        const next = !row.is_active;
        /* Optimistic flip, revert on failure. */
        setRows((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r, is_active: next } : r)),
        );
        try {
            await setUserStatus(row.id, next);
        } catch (err) {
            setRows((prev) =>
                prev.map((r) =>
                    r.id === row.id ? { ...r, is_active: row.is_active } : r,
                ),
            );
            toast.error(apiError(err, "Could not change the status"));
        }
    }

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Name",
                render: (row) => (
                    <Stack sx={{ gap: 0.25 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {fullName(row)}
                        </Typography>
                        {row.designation && (
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                {row.designation}
                            </Typography>
                        )}
                    </Stack>
                ),
            },
            { key: "email", label: "Email" },
            {
                key: "org_role_name",
                label: "Role",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <Chip
                            size="small"
                            label={row.org_role_name || "—"}
                            sx={{
                                height: 22,
                                background: "var(--bg-tint-soft)",
                                border: "1px solid var(--border-mid)",
                                color: "var(--text-strong-muted)",
                                fontWeight: 600,
                            }}
                        />
                        {row.is_super_admin && (
                            <Chip
                                size="small"
                                label="Super admin"
                                sx={{
                                    height: 20,
                                    fontSize: 11,
                                    background: "var(--accent-soft)",
                                    border: "1px solid var(--accent)",
                                    color: "var(--accent)",
                                }}
                            />
                        )}
                    </Stack>
                ),
            },
            {
                key: "manager_name",
                label: "Manager",
                render: (row) => row.manager_name || "—",
            },
            {
                key: "is_active",
                label: "Status",
                render: (row) => (
                    <Switch
                        size="small"
                        checked={!!row.is_active}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleStatusToggle(row)}
                    />
                ),
            },
            {
                key: "last_login_at",
                label: "Last login",
                render: (row) => formatDateTime(row.last_login_at),
            },
            {
                key: "actions",
                label: "",
                align: "right",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            gap: 0.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Edit"
                            onClick={() => setEditing(row)}
                        >
                            <EditRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Reset password"
                            onClick={() => setResetting(row)}
                        >
                            <KeyRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="Team"
                subtitle="Who's in the workspace, what they can do, and who they report to"
                action={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddAltRounded />}
                        onClick={() => setInviteOpen(true)}
                    >
                        Invite user
                    </Button>
                }
            />

            <Stack sx={{ gap: 2 }}>
                <TextField
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ maxWidth: 320 }}
                />

                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={(page) =>
                        load(page, pagination.limit, search)
                    }
                    onLimitChange={(limit) => load(1, limit, search)}
                    onRowClick={(row) => setEditing(row)}
                    emptyMessage="No team members yet — send the first invite"
                />
            </Stack>

            <InviteDialog
                open={inviteOpen}
                roles={roles}
                assignable={assignable}
                onClose={() => setInviteOpen(false)}
                onInvited={() => {
                    setInviteOpen(false);
                    reload();
                    loadPickers();
                }}
            />

            <EditDrawer
                user={editing}
                roles={roles}
                assignable={assignable}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    reload();
                    loadPickers();
                }}
            />

            <ResetPasswordDialog
                user={resetting}
                onClose={() => setResetting(null)}
            />
        </Stack>
    );
}
